import React, { Fragment, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Modal from 'react-modal';
import { useHistory, useRouteMatch } from 'react-router-dom';
import QRCode from 'qrcode.react';

import { IMAGE_SIZE_CODE } from '../../../../constants/image-size-codes';
import Spinner from '../../../global/Spinner';
import Button from '../../../global/Button';
import Angle from '../../../global/Angle';
import ProgressivePhoto from '../../ProgressivePhoto';
import ImagePreload from '../../../global/ImagePreload';
import AlbumSlideshowThumbnailMenu from './components/AlbumSlideshowThumbnailMenu';
import useSlideshowPlayback from './hooks/useSlideshowPlayback';
import PhotoUploadCache from '../../../product/PhotoUploadCache';
import { getThumbBySize } from '../../../../util/photo';
import { getAsset } from '../../../../util/assets';
import getConfig from '../../../../util/config';
import AccountCard from '../../../../services/cards/components/AccountCard';
import {
  getSlideshowQRInsertUrl,
  isIntroLayout,
} from '../../../../apps/pro/views/Album/AlbumSlideshow/AlbumSlideshowDesignerView/helpers';
import IntroSlideLabel from '../../../../apps/pro/views/Album/MemoryBookAlbum/components/MemoryBookIntroSlide/components/IntroSlideLabel';
import { isMobile } from '../../../../util/device-detector';
import DismissCircleIcon from '../../../icons/DismissCircleIcon';

export const LOAD_MORE_THRESHOLD = 5;

export const DISPLAY_MODE = {
  PRESENT: 'PRESENT',
  PREVIEW: 'PREVIEW',
};

const propTypes = {
  album: PropTypes.shape({
    id: PropTypes.string,
    logo_image: PropTypes.shape({
      url: PropTypes.string,
    }),
  }),
  slideshow: PropTypes.shape({
    id: PropTypes.string,
    qr_code: PropTypes.shape({
      is_enabled: PropTypes.bool,
      display_callout: PropTypes.bool,
    }),
    ticker: PropTypes.shape({
      message: PropTypes.string,
      color: PropTypes.string,
      background_color: PropTypes.string,
    }),
  }).isRequired,
  recordId: PropTypes.string,
  records: PropTypes.arrayOf(
    PropTypes.shape({
      photo: PropTypes.shape({
        id: PropTypes.string,
      }).isRequired,
    }),
  ),
  recordsTotal: PropTypes.number,
  loadMore: PropTypes.func,
  loadingMore: PropTypes.bool,
  shouldLoadMore: PropTypes.bool,
  recordSlideAccessor: PropTypes.string,
  renderSlideshowSlide: PropTypes.func,
  actions: PropTypes.func,
  imageSizeCode: PropTypes.oneOf(Object.values(IMAGE_SIZE_CODE)),
  gridPath: PropTypes.string,
  dismissible: PropTypes.bool,
  onDismiss: PropTypes.func,
  displayMode: PropTypes.oneOf(Object.values(DISPLAY_MODE)),
  renderTitle: PropTypes.func,
  onDeletePhoto: PropTypes.func,
  isAutoLayout: PropTypes.bool,
  useMobileFullScreen: PropTypes.bool,
  includeWaldoLogo: PropTypes.bool,
};

const AlbumSlideshowGridViewer = ({
  album,
  slideshow,
  records,
  recordId,
  recordsTotal,
  shouldLoadMore,
  loadMore,
  loadingMore,
  recordSlideAccessor,
  renderSlideshowSlide,
  imageSizeCode = IMAGE_SIZE_CODE.LARGE,
  actions,
  gridPath,
  dismissible = true,
  onDismiss,
  displayMode = DISPLAY_MODE.PREVIEW,
  renderTitle,
  onDeletePhoto,
  isAutoLayout,
  useMobileFullScreen,
  includeWaldoLogo = true,
}) => {
  const match = useRouteMatch();
  const history = useHistory();
  const { app } = getConfig();
  const qrCodeUrl = getSlideshowQRInsertUrl(app?.baseUrl, album);
  const [hideMenus, setHideMenus] = useState(false);
  const [imagePreloadComponent, setImagePreloadComponent] = useState(null);
  const hideMenusTimer = useRef();
  const config = getConfig();

  const onMouseMove = () => {
    setHideMenus(false);
    if (hideMenusTimer.current) {
      clearTimeout(hideMenusTimer.current);
      hideMenusTimer.current = null;
    }

    hideMenusTimer.current = setTimeout(() => setHideMenus(true), 4000);
  };

  const { url } = match;
  const basePath = url.split('/').slice(0, -1).join('/');

  useEffect(() => {
    window.addEventListener('mousemove', onMouseMove);
    window.focus();
    // Trigger a mouse move. This ensures that the menu will be
    // hiding even if the user doesn't move the mouse at all.
    onMouseMove();

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      //  Clear the timeout if it exists by this point
      if (hideMenusTimer.current) {
        clearTimeout(hideMenusTimer.current);
        hideMenusTimer.current = null;
      }
    };
  }, []);

  const modalClasses = classNames({
    'slideshow-grid-viewer': true,
    'hide-until-move': hideMenus,
  });

  const headerClasses = classNames({
    'slideshow-grid-viewer-header': true,
    'hide-until-move': hideMenus,
  });

  const bodyClasses = classNames({
    'slideshow-grid-viewer-body': true,
    'full-until-move': hideMenus,
  });

  const footerClasses = classNames({
    'slideshow-grid-viewer-footer': true,
    'hide-until-move': hideMenus,
    // Remove this in order to re-add the thumbnail menu if needed.
    'd-none': true,
  });

  /**
   * Checks if more records are necessary to load and loads them
   * if we're approaching the end of the navigation (right-hand-wise)
   * so we would need to load more records to continue to the next record.
   *
   * @returns {Promise<void>}
   */
  const maybeLoadMoreRecords = async () => {
    if (loadingMore || !loadMore || !shouldLoadMore) {
      return;
    }

    const currentRecordIndex = records.findIndex(
      ({ photo, asset_image, account_card }) =>
        (photo && recordId === photo.id) ||
        (asset_image && recordId === asset_image.id) ||
        (account_card && recordId === account_card.id),
    );

    // If we're approaching the end and there are more records, load them
    // to keep a steady progression to the next record
    if (
      records.length - currentRecordIndex <= LOAD_MORE_THRESHOLD ||
      // If we land on a slide not loaded yet, we need to query stuff until we get it
      currentRecordIndex === -1
    ) {
      await loadMore({ updateTotals: true });
    }
  };

  useEffect(() => {
    maybeLoadMoreRecords();
  }, [recordId, recordsTotal, records.length, loadingMore, shouldLoadMore]);

  /**
   * Gets the adjacent record ID (ie previous or next one).
   *
   * @param {number} adjacency - 1 for "next" or -1 for "previous".
   * @returns {string|undefined}
   */
  const getAdjacentRecordId = adjacency => {
    const currentRecordIndex = records.findIndex(
      ({ photo, asset_image, account_card }) =>
        (photo && recordId === photo.id) ||
        (asset_image && recordId === asset_image.id) ||
        (account_card && recordId === account_card.id),
    );
    const nextRecord = records[currentRecordIndex + 1];
    const prevRecord = records[currentRecordIndex - 1];

    if (currentRecordIndex === -1) {
      return undefined;
    }

    if (adjacency === 1 && nextRecord) {
      return (
        nextRecord.asset_image?.id ??
        nextRecord.photo?.id ??
        nextRecord.account_card?.id
      );
    }

    if (adjacency === -1 && prevRecord) {
      return (
        prevRecord.asset_image?.id ??
        prevRecord.photo?.id ??
        prevRecord.account_card?.id
      );
    }

    return undefined;
  };

  /**
   * Gets the adjacent record index (ie previous or next one).
   *
   * @param {number} adjacency - 1 for "next" or -1 for "previous".
   * @returns {number|undefined}
   */
  const getAdjacentRecordIndex = adjacency => {
    const currentRecordIndex = records.findIndex(
      ({ photo, asset_image, account_card }) =>
        (photo && recordId === photo.id) ||
        (asset_image && recordId === asset_image.id) ||
        (account_card && recordId === account_card.id),
    );
    const nextRecord = currentRecordIndex + 1;
    const prevRecord = currentRecordIndex - 1;

    if (currentRecordIndex === -1) {
      return undefined;
    }

    if (adjacency === 1 && nextRecord < records.length) {
      return nextRecord;
    }

    if (adjacency === -1 && prevRecord > -1) {
      return prevRecord;
    }

    return undefined;
  };

  /**
   * Dismisses the Slide Viewer
   */
  const onDismissSlideshow = () => {
    if (onDismiss) {
      onDismiss();
    } else {
      history.replace(gridPath);
    }
  };

  /**
   * Helper method that navigates to either the next or prev record
   * depending on whether they exist (with that priority) or dismisses
   * the slide viewer in case neither exist.
   */
  const onNavigateNextAfterRemoval = () => {
    const nextRecordId = getAdjacentRecordId(1);

    if (!nextRecordId) {
      const prevRecordId = getAdjacentRecordId(-1);

      if (!prevRecordId) {
        onDismissSlideshow();
      } else {
        history.replace(`${basePath}/${prevRecordId}`);
      }

      return;
    }

    history.replace(`${basePath}/${nextRecordId}`);
  };

  const getSlideFromRecord = record => {
    if (!record) {
      return undefined;
    }

    return recordSlideAccessor ? record?.[recordSlideAccessor] : record;
  };

  const record = records.find(
    ({ photo, asset_image, account_card }) =>
      (photo && recordId === photo.id) ||
      (asset_image && recordId === asset_image.id) ||
      (account_card && recordId === account_card.id),
  );
  const nextRecordId = getAdjacentRecordId(1);
  const nextRecordIdx = getAdjacentRecordIndex(1);
  const prevRecordId = getAdjacentRecordId(-1);
  const prevRecordIdx = getAdjacentRecordIndex(-1);

  const accountLogo = album?.creator?.logo_image;
  const slideshowSlide = getSlideFromRecord(record);
  const nextSlideshowSlide = getSlideFromRecord(
    records.find(
      ({ photo, asset_image, account_card }) =>
        (photo && nextRecordId === photo.id) ||
        (asset_image && nextRecordId === asset_image.id) ||
        (account_card && nextRecordId === account_card.id),
    ),
  );

  // Avoids an infinite loop of having <ImagePreload /> render based off
  // nextSlideshowSlide alone. This is because if we do so, the ImagePreload
  // renders a new iteration every time, which results in requesting the same
  // url over and over until the photo changes, which then result in this
  // looping back again.
  useEffect(() => {
    if (nextSlideshowSlide?.photo) {
      setImagePreloadComponent(
        <ImagePreload
          urls={[
            getThumbBySize(
              nextSlideshowSlide.photo.thumbnail_urls,
              imageSizeCode,
              true,
            )?.url,
          ]}
        />,
      );
    } else if (nextSlideshowSlide?.asset_image) {
      setImagePreloadComponent(
        <ImagePreload urls={[nextSlideshowSlide.asset_image.url]} />,
      );
    } else if (nextSlideshowSlide?.account_card?.front_preview_file_url) {
      setImagePreloadComponent(
        <ImagePreload
          urls={[nextSlideshowSlide.account_card.front_preview_file_url]}
        />,
      );
    }
  }, [nextSlideshowSlide]);

  const slideshowDuration =
    slideshow.slide_duration_ms *
    ((recordsTotal ?? 0) + (slideshow.intro_layout ? 1 : 0));

  const {
    isPlaying,
    onPlaybackStart,
    onPlaybackStop,
    onThumbnailClick,
    elapsedTime,
    slideDuration,
    slideRemainingTime,
    skipFadeIn,
  } = useSlideshowPlayback({
    slideshowSlides: records,
    slideshowSlide,
    slideshowSlideIdx: records.findIndex(
      ({ photo, asset_image, account_card }) =>
        (photo && recordId === photo.id) ||
        (asset_image && recordId === asset_image.id) ||
        (account_card && recordId === account_card.id),
    ),
    nextSlideId:
      nextSlideshowSlide?.asset_image?.id ??
      nextSlideshowSlide?.photo?.id ??
      nextSlideshowSlide?.account_card?.id,
    slideDurationMs: slideshow.slide_duration_ms,
    slideshowDuration,
    intervalValue: 50,
    onChangeSlide: ({ nextSlideId }) => {
      history.replace(`${basePath}/${nextSlideId}`);
    },
  });

  const photoOpacity = (() => {
    const fadeThreshold = slideDuration * 0.2;
    // If we have > duration - threshold remaining time (fade-out)
    if (!skipFadeIn && slideRemainingTime > slideDuration - fadeThreshold) {
      return (slideDuration - slideRemainingTime) / 1000.0;
    }
    // If we have < remaining time (fade-out)
    if (slideRemainingTime <= fadeThreshold) {
      return slideRemainingTime / fadeThreshold;
    }
    return 1.0;
  })();

  return (
    <Modal
      ariaHideApp={false}
      isOpen
      bodyOpenClassName="body-modal-open"
      overlayClassName={classNames({
        'slideshow-grid-viewer-overlay': true,
        'mobile-fullscreen': !!useMobileFullScreen,
      })}
      className={modalClasses}
      style={{
        overlay: {
          backgroundColor: slideshow?.slide_background_color,
          backgroundImage: slideshow?.slide_background_asset_image
            ? `url(${slideshow.slide_background_asset_image.url})`
            : '',
        },
      }}
    >
      {accountLogo && !dismissible && (
        <img
          className="slideshow-grid-viewer-account-logo"
          src={accountLogo.url}
          alt="Account Logo"
        />
      )}
      {includeWaldoLogo &&
        config?.marcomUrl &&
        displayMode === DISPLAY_MODE.PRESENT && (
          <a
            className="slideshow-grid-viewer-waldo-logo"
            href={config.marcomUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src={getAsset(`/powered-by-waldo.svg`)} alt="Waldo Logo" />
          </a>
        )}
      {dismissible && (
        <Button
          btnStyle="reset"
          onClick={() => {
            onPlaybackStop();
            onDismissSlideshow();
          }}
          className="slideshow-grid-viewer-btn-dismiss text-lg"
        >
          <Angle direction="left" className="d-none d-md-inline-block" />{' '}
          <span className="d-none d-md-inline-block ml-1">Back</span>
          <DismissCircleIcon
            className="d-md-none"
            size={32}
            color="#FFF"
            crossColor="#1786f6"
          />
        </Button>
      )}
      <div className={headerClasses}>
        <div className="slideshow-grid-viewer-header-left" />

        <div className="slideshow-grid-viewer-header-center">
          {renderTitle && renderTitle()}
          {!renderTitle && (
            <h3 className="w-100 text-white text-center mb-0">
              {slideshow.name}
            </h3>
          )}
        </div>

        {actions && (
          <div className="slideshow-grid-viewer-header-right">
            {actions({
              record,
              onPrevRecord: () =>
                onThumbnailClick({
                  slideId: prevRecordId,
                  slideIdx: prevRecordIdx,
                }),
              onNextRecord: () =>
                onThumbnailClick({
                  slideId: nextRecordId,
                  slideIdx: nextRecordIdx,
                }),
              onNavigateNextAfterRemoval,
              nextRecordId,
              prevRecordId,
              playbackActions: {
                onPlaybackStop,
                onPlaybackStart,
              },
            })}
          </div>
        )}
      </div>
      <div className={bodyClasses}>
        <div className="slideshow-grid-viewer-content">
          {slideshowSlide ? (
            (() => {
              if (renderSlideshowSlide) {
                return renderSlideshowSlide({ slideshowSlide, record });
              }

              if (
                slideshowSlide.account_card &&
                !slideshowSlide.account_card.front_preview_file_url
              ) {
                return (
                  <div
                    className="w-100"
                    style={isPlaying ? { opacity: photoOpacity } : undefined}
                  >
                    {isIntroLayout(slideshowSlide) && (
                      <IntroSlideLabel albumId={album.id} />
                    )}
                    <AccountCard
                      onElementConfigurationChange={() => {}}
                      accountCard={slideshowSlide.account_card}
                      animatable={false}
                      viewOnly
                      disablePhotoWatermarkOverlay
                    />
                  </div>
                );
              }

              return (
                <Fragment>
                  {isIntroLayout(slideshowSlide) && (
                    <IntroSlideLabel
                      albumId={album.id}
                      style={isPlaying ? { opacity: photoOpacity } : undefined}
                    />
                  )}

                  <PhotoUploadCache
                    id={
                      slideshowSlide.asset_image?.id ??
                      slideshowSlide.photo?.id ??
                      slideshowSlide.account_card?.id
                    }
                    thumbs={(() => {
                      if (slideshowSlide.asset_image) {
                        return [
                          {
                            width: 1,
                            height: 1,
                            size_code: imageSizeCode,
                            ...slideshowSlide.asset_image,
                          },
                        ];
                      }

                      if (slideshowSlide.account_card) {
                        const { account_card } = slideshowSlide;
                        return [
                          {
                            width: 1,
                            height: 1,
                            size_code: imageSizeCode,
                            id: account_card?.id,
                            url: account_card?.front_preview_file_url,
                          },
                        ];
                      }

                      return (
                        slideshowSlide.photo?.watermarked_urls ??
                        slideshowSlide.photo?.thumbnail_urls ??
                        []
                      );
                    })()}
                    render={thumbs => (
                      <ProgressivePhoto
                        key={recordId}
                        id={recordId}
                        thumbnails={thumbs}
                        size={imageSizeCode}
                        allowFallbackThumbnail
                        imgProps={{
                          style: isPlaying
                            ? { opacity: photoOpacity }
                            : undefined,
                        }}
                      />
                    )}
                  />
                </Fragment>
              );
            })()
          ) : (
            <Fragment>
              {records.length > 0 ? (
                <Spinner />
              ) : (
                <div className="text-center text-white text-extra-bold">
                  <img
                    width="40px"
                    src={getAsset(`/waldo-icon.svg`)}
                    alt="Waldo"
                    className="mb-2"
                  />
                  <p>No slides yet.</p>
                </div>
              )}
            </Fragment>
          )}
        </div>
      </div>
      <div className="slideshow-grid-viewer-pre-footer">
        {slideshow.qr_code?.is_enabled && !isMobile() && qrCodeUrl && (
          <div className="qr-code-container">
            <QRCode
              id="got-photos-qr"
              value={qrCodeUrl}
              renderAs="canvas"
              size={100}
            />
            {slideshow.qr_code.display_callout && (
              <div className="qr-code-callout">SCAN TO ADD PHOTOS</div>
            )}
          </div>
        )}
        {slideshow?.ticker?.message && (
          <div
            className="ticket-tape-container"
            style={{
              backgroundColor: slideshow.ticker.background_color,
            }}
          >
            <p style={{ color: slideshow.ticker.color }}>
              {slideshow.ticker.message}
            </p>
          </div>
        )}
      </div>
      <div className={footerClasses}>
        {slideshow && displayMode !== DISPLAY_MODE.PRESENT && (
          <AlbumSlideshowThumbnailMenu
            slideshowSlides={records}
            slidesTotal={(recordsTotal ?? 0) + (slideshow.intro_layout ? 1 : 0)}
            slidesOffset={Math.max((records.length ?? 10) - 10, 0)}
            elapsedTime={elapsedTime}
            defaultDuration={slideshow.slide_duration_ms}
            totalDuration={slideshowDuration}
            isPlaying={isPlaying}
            onPlayToggle={() =>
              isPlaying ? onPlaybackStop() : onPlaybackStart()
            }
            onThumbnailClick={onThumbnailClick}
            currentSlideId={
              record?.asset_image?.id ??
              record?.photo?.id ??
              record?.account_card?.id
            }
            currentSlideDuration={slideDuration}
            currentSlideRemainingTime={slideRemainingTime}
            onDeletePhoto={
              onDeletePhoto && !isAutoLayout
                ? async ({ photoId, isAssetImage = false }) => {
                    await onDeletePhoto({ photoId, isAssetImage });
                    onNavigateNextAfterRemoval();
                  }
                : undefined
            }
            loadingMore={loadingMore}
            onLoadMore={loadMore}
            isAutoLayout={isAutoLayout}
          />
        )}
      </div>
      {imagePreloadComponent}
    </Modal>
  );
};

AlbumSlideshowGridViewer.propTypes = propTypes;

export default AlbumSlideshowGridViewer;
